import React, { Fragment, useEffect, useRef, useState } from "react";
import { App, Button, Modal, Space, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { specialOutDo, specialOutlots } from "@/apis/requirement";
import {
  InventoryLotItem,
  InventoryLocation
} from "@/types/inventoryV2";
import { CommonListParams } from "@/types/common";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef
} from "@/styles/STableLayout";
import useDataList from "@/hooks/useDataList";
import SText from "@/styles/SText";
import { AnyObject } from "antd/es/_util/type";
import { getLocationCode } from "@/pages/Warehouse/utils";
import { EyeOutlined } from "@ant-design/icons";
import PreviewLocationCode from "@/pages/Warehouse/components/PreviewLocationCode";
import SpecialOutPrint from "./SpecialOutPrint";
import SpecialOutInput from "./SpecialOutInput";
import { TableRowSelection } from "antd/es/table/interface";
import { RequirementItem } from "@/types/requirement";
import ExportExcelButton, {
  ExportColumn
} from "@/components/ExportExcelButton";

interface SpecialOutProps {
  reqId: string;
  reqNumber: string;
  visible: boolean;
  onClose: () => void;
  readOnly?: boolean;
  showItems?: RequirementItem[];
}

const SpecialOut: React.FC<SpecialOutProps> = ({
  reqId,
  reqNumber,
  visible,
  onClose,
  readOnly = false,
  showItems
}) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const { notification } = App.useApp();

  const [outLoading, setOutLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<InventoryLotItem[]>([]);
  const [outInputVisible, setOutInputVisible] = useState<boolean>(false);

  const [list, total, loading, { fetchList }] = useDataList<
    InventoryLotItem,
    [string, CommonListParams]
  >({
    fetchApi: specialOutlots
  });

  // 导出列定义
  const exportColumns: ExportColumn[] = [
    { key: "productCode", label: t("product.code") },
    { key: "productName", label: t("product.name") },
    { key: "productModel", label: t("product.brand1") },
    { key: "uom", label: t("product.uom") },
    { key: "materialProcess", label: t("product.materialProcess") },
    { key: "lot", label: t("warehouse.batchNo") },
    { key: "thdLot", label: t("warehouse.thirdPartyBatch") },
    { key: "count", label: t("order.quantity") },
    { key: "label1", label: t("warehouse.labels") },
    { key: "locations", label: t("warehouse.location") }
  ];

  // 数据映射函数
  const mapDataToExcel = (item: InventoryLotItem, t: any) => ({
    [t("product.code")]: item.product?.code || "",
    [t("product.name")]: item.product?.name || "",
    [t("product.brand1")]: item.product?.model || "",
    [t("product.uom")]: item.product?.uom || "",
    [t("product.materialProcess")]: item.product?.process || "",
    [t("warehouse.batchNo")]: item.lot?.lot || "",
    [t("warehouse.thirdPartyBatch")]: item.lot?.thdLot || "",
    [t("order.quantity")]: item.count ?? 0,
    [t("warehouse.labels")]: (
      item.locations?.map(loc => loc.location?.label1 ?? "") || []
    ).join(", "),
    [t("warehouse.location")]: (
      item.locations?.map(loc => getLocationCode(loc.location) ?? "") || []
    ).join(", ")
  });

  const [previewBarcodes, setPreviewBarcodes] = useState<{
    visible: boolean;
    locations: InventoryLocation[];
    productName: string;
  }>({
    visible: false,
    locations: [],
    productName: ""
  });

  useEffect(() => {
    if (reqId && visible) {
      tableRef?.current?.resetTablePage();
      fetch(0);
    }
  }, [reqId, visible]);

  const fetch = (offset: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    const params: CommonListParams = {
      pagination: { offset, take }
    };
    if (reqId) {
      fetchList(reqId, params);
    }
  };

  const columns: ColumnsType<InventoryLotItem> = [
    {
      title: t("purchase.productInfo"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          key: "productCode",
          width: 120,
          render: (_, record) => (
            <SText
              text={record.product?.code || ""}
              style={{ width: "100%" }}
            />
          )
        },
        {
          title: <SText text={t("product.name")} />,
          key: "productName",
          width: 150,
          render: (_, record) => (
            <SText
              text={record.product?.name || ""}
              style={{ width: "100%" }}
            />
          )
        },
        {
          title: <SText text={t("product.brand1")} />,
          key: "productModel",
          width: 120,
          render: (_, record) => (
            <SText
              text={record.product?.model || ""}
              style={{ width: "100%" }}
            />
          )
        },
        {
          title: <SText text={t("product.uom")} />,
          key: "uom",
          width: 80,
          align: "center",
          render: (_, record) => (
            <SText text={record.product?.uom || ""} style={{ width: "100%" }} />
          )
        },
        {
          title: <SText text={t("product.materialProcess")} />,
          key: "materialProcess",
          width: 160,
          render: (_, record) => (
            <SText text={record.product?.process || ""} style={{ width: "100%" }} />
          )
        }
      ]
    },
    {
      title: <SText text={t("warehouse.batchNo")} />,
      key: "lot",
      width: 120,
      render: (_, record) => (
        <SText text={record.lot?.lot || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("warehouse.thirdPartyBatch")} />,
      key: "thdLot",
      width: 120,
      render: (_, record) => (
        <SText text={record.lot?.thdLot || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("order.quantity")} />,
      dataIndex: "count",
      key: "count",
      width: 100,
      align: "center",
      render: text => (
        <SText text={text?.toString() || "0"} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("warehouse.labels")} />,
      dataIndex: "label1",
      key: "label1",
      width: 120,
      align: "center",
      render(_, record) {
        return (
          <SText
            text={(
              record.locations?.map(loc => loc.location?.label1 ?? "") || []
            ).join(", ")}
          />
        );
      }
    },
    {
      title: <SText text={t("warehouse.location")} />,
      key: "locations",
      width: 240,
      align: "center",
      render(_, record) {
        return (
          <SText
            text={(
              record.locations?.map(
                loc => getLocationCode(loc.location) ?? ""
              ) || []
            ).join(", ")}
          />
        );
      }
    },
    {
      title: t("common.opt"),
      key: "action",
      width: 160,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setPreviewBarcodes({
              visible: true,
              locations: record.locations || [],
              productName: record.product?.name || ""
            });
          }}
        >
          {t("warehouse.viewBarcode")}
        </Button>
      )
    }
  ];

  const rowSelection: TableRowSelection<AnyObject> = {
    selectedRowKeys: selectedRows.map(item => item.id),
    onChange: (_, selectedRows) => {
      setSelectedRows(selectedRows as InventoryLotItem[]);
    }
  };

  // const dataList: Array<InventoryLotItem> = useMemo(() => {
  //   let showProductIds = showItems?.filter(item => !!item.puredCount && !!item.inCount && Number(item.puredCount) === Number(item.inCount))?.map(it => it.productId) || []
  //   if (showProductIds && showProductIds.length > 0) return list.filter(item => showProductIds.includes(item.productId))
  //   else return list
  // }, [list, showItems])

  const doOut = () => {
    setOutLoading(true);
    specialOutDo(reqId ?? "")
      .then(() => {
        notification.success({
          placement: "top",
          message: t("warehouse.specialOutSuccess")
        });
        onClose();
      })
      .catch(() => {
        notification.error({
          placement: "top",
          message: t("warehouse.specialOutFail")
        });
      })
      .finally(() => setOutLoading(false));
  };

  const handleOut = () => {
    setOutInputVisible(true);
  };

  const handleOutInputClose = () => {
    setOutInputVisible(false);
    setSelectedRows([]);
  };

  return (
    <Fragment>
      <Modal
        maskClosable={false}
        title={
          <Space>
            <span>{t("warehouse.specialOutbound")}</span>
            <Typography.Link>{reqNumber}</Typography.Link>
            <SpecialOutPrint
              reqNumber={reqNumber}
              datas={selectedRows}
              disabled={loading || selectedRows.length === 0}
            />
            <Button
              onClick={handleOut}
              disabled={readOnly || selectedRows.length === 0}
            >
              {t("inventory.outbound")}
            </Button>
            <ExportExcelButton
              columns={exportColumns}
              defaultSelectedColumns={[
                "productCode",
                "productName",
                "productModel",
                "materialProcess",
                "uom",
                "lot",
                "thdLot",
                "count",
                "label1",
                "locations"
              ]}
              dataMapper={mapDataToExcel}
              list={list}
              fileName={`${t("warehouse.specialOutbound")}_${reqNumber}.xlsx`}
              btnText={t("warehouse.exportExcel")}
            />
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1200}
        destroyOnClose
        onOk={doOut}
        okButtonProps={{ loading: outLoading, disabled: loading || readOnly }}
        footer={null}
      >
        <STableLayout
          headerChildren={null}
          tableLayoutRef={tableRef}
          columns={columns as ColumnsType<AnyObject>}
          dataSource={list}
          total={total}
          loading={loading}
          handleChange={fetch}
          rowKey="id"
          rowSelection={rowSelection}
          scroll={{ x: 1200 }}
        />
      </Modal>
      <SpecialOutInput
        reqNumber={reqNumber}
        visible={outInputVisible}
        onClose={handleOutInputClose}
        selectedRows={selectedRows}
        showItems={showItems}
      />
      <PreviewLocationCode
        visible={previewBarcodes.visible}
        onClose={() =>
          setPreviewBarcodes({ visible: false, locations: [], productName: "" })
        }
        locationBarcodes={previewBarcodes.locations.map(loc => ({
          count: loc.count,
          location: loc.location,
          barcodes: loc.barcodes || []
        }))}
        title={`${t("warehouse.viewBarcode")} - ${previewBarcodes.productName}`}
      />
    </Fragment>
  );
};

export default SpecialOut;
